<template>
  <div v-if="!$store.state.userProfile">Please log in to track games.</div>
  <div v-else>
    <b-row>
      <b-col>
        <b-form-group id="my-name-group" label="Your name" label-for="my-name">
          <b-form-input
            id="my-name"
            :value="$store.getters.currentUser"
            readonly
          ></b-form-input>
        </b-form-group>
        <b-form-group
          id="my-character-group"
          label="Your character"
          label-for="my-character"
          invalid-feedback="Choose your character"
          :state="myCharacterId !== null"
        >
          <vue-typeahead-bootstrap
            v-model="myCharacter"
            @hit="myCharacterId = $event.id"
            showOnFocus
            :data="characters"
            :serializer="(item) => item.name"
            placeholder="Select a character"
            @input="lookupMyCharacters"
          >
            <template slot="suggestion" slot-scope="{ data, htmlText }">
              <div>
                <span v-html="htmlText"></span>
                <span class="small"> {{ data.version_name }}</span>
                <div v-if="data.creator" class="small">
                  By {{ data.creator }}
                </div>
              </div>
            </template>
          </vue-typeahead-bootstrap>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          id="vs-name-group"
          label="Opponent's name (optional)"
          label-for="vs-name"
        >
          <b-form-input
            id="vs-name"
            v-model="opponentName"
            placeholder="Type your opponent's name"
          ></b-form-input>
        </b-form-group>
        <b-form-group
          id="vs-character-group"
          label="Opponent's character"
          label-for="vs-character"
          invalid-feedback="Choose your opponent's character"
          :state="vsCharacterId !== null"
        >
          <vue-typeahead-bootstrap
            v-model="vsCharacter"
            @hit="vsCharacterId = $event.id"
            showOnFocus
            :data="characters"
            :serializer="(item) => item.name"
            placeholder="Select a character"
            @input="lookupVsCharacters"
          >
            <template slot="suggestion" slot-scope="{ data, htmlText }">
              <div>
                <span v-html="htmlText"></span>
                <span class="small"> {{ data.version_name }}</span>
                <div v-if="data.creator" class="small">
                  By {{ data.creator }}
                </div>
              </div>
            </template>
          </vue-typeahead-bootstrap>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group
          label="Who won?"
          invalid-feedback="Choose the winner"
          :state="winner !== null"
        >
          <b-form-radio-group
            id="winner-radio-group"
            v-model="winner"
            name="winner-radios"
          >
            <b-form-radio :value="true">{{
              myCharacter || "You"
            }}</b-form-radio>
            <b-form-radio :value="false">{{
              vsCharacter || "Opponent"
            }}</b-form-radio>
          </b-form-radio-group>
        </b-form-group>
        <b-form-group label="Who went first? (Optional)">
          <b-form-radio-group
            id="first-radio-group"
            v-model="first"
            name="first-radios"
          >
            <b-form-radio :value="true">{{
              myCharacter || "You"
            }}</b-form-radio>
            <b-form-radio :value="false">{{
              vsCharacter || "Opponent"
            }}</b-form-radio>
            <b-form-radio :value="null">N/A</b-form-radio>
          </b-form-radio-group>
        </b-form-group>
        <b-form-group
          id="board-group"
          label="Board (optional)"
          label-for="board"
        >
          <b-form-select
            v-model="boardSlug"
            :options="boardOptions"
          ></b-form-select>
        </b-form-group>
        <b-form-group
          id="rounds-input-group"
          label="How many rounds did the game last? (optional)"
          label-for="rounds-input"
        >
          <b-form-input
            id="rounds"
            min="0"
            max="30"
            type="number"
            v-model="rounds"
            placeholder="Type the number of rounds the game lasted"
          ></b-form-input>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group
          id="my-hp-group"
          label="Your final HP (optional)"
          label-for="my-hp"
        >
          <b-form-input
            id="my-hp"
            type="number"
            v-model="myHp"
            placeholder="Type your final HP (can be negative)"
          ></b-form-input>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          id="vs-hp-group"
          label="Opponent's final HP (optional)"
          label-for="vs-hp"
        >
          <b-form-input
            id="vs-hp"
            type="number"
            v-model="vsHp"
            placeholder="Type your opponent's final HP (can be negative)"
          ></b-form-input>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group
          id="played-on-group"
          label="When did you play? (optional)"
          label-for="played-on-input"
        >
          <b-form-datepicker
            id="played-on-input"
            v-model="playedOn"
            :max="today"
            locale="en"
            reset-button
          ></b-form-datepicker>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group
          id="rating-input-group"
          label="How would you rate this game? (optional)"
          label-for="rating-input"
        >
          <b-form-rating v-model="rating" show-clear></b-form-rating>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          id="notes-input-group"
          label="Notes (optional)"
          label-for="notes-input"
        >
          <b-form-textarea
            id="textarea"
            v-model="notes"
            placeholder="My game notes"
            rows="3"
          ></b-form-textarea>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-checkbox id="is-public" v-model="isPublic" name="is-public">
          Allow other users to see the details of this game (including rating
          and notes).
        </b-form-checkbox>
        <b-form-checkbox id="is-counted" v-model="isCounted" name="is-counted">
          Include this game in public statistics.
          <strong>
            Please make sure only ONE player checks this box to avoid double
            counting!
          </strong>
          <span v-if="gameId === null" class="text-info">
            For official decks, checking this box will sync this record to the
            <b-link
              href="https://docs.google.com/spreadsheets/d/1BOdYOeTDW0ddVBq4Oq_CWADMzIonOcBjvDRpWHuUs50/edit#gid=2027579905"
              class="text-info alert-link"
              >Google tracking sheet</b-link
            >.
          </span>
        </b-form-checkbox>
      </b-col>
    </b-row>
    <b-row class="mt-4">
      <b-col>
        <b-button v-if="gameId == null" :disabled="!isValid" @click="saveGame">
          Save
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import api from "@/api";

import VueTypeaheadBootstrap from "vue-typeahead-bootstrap";

export default {
  name: "TrackGameForm",
  components: {
    VueTypeaheadBootstrap,
  },
  props: {
    game: {
      type: Object,
      required: false,
    },
  },
  data() {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return {
      gameId: this.game?.id ?? null,
      myCharacter: this.game?.as.name ?? "",
      myCharacterId: this.game?.as.id ?? null,
      vsCharacter: this.game?.vs.name ?? "",
      vsCharacterId: this.game?.vs.id ?? null,
      opponentName: this.game?.opponent_name ?? "",
      boardSlug: this.game?.board ?? null,
      winner: this.game?.won ?? null,
      first: this.game?.went_first ?? null,
      isPublic: this.game?.is_public ?? false,
      isCounted: this.game?.is_counted ?? false,
      characters: [],
      myHp: (this.game?.won ? this.game?.winner_hp : this.loser_hp) ?? null,
      vsHp: (this.game?.won ? this.game?.loser_hp : this.winner_hp) ?? null,
      rounds: this.game?.rounds ?? null,
      rating: this.game?.rating ?? null,
      notes: this.game?.notes ?? null,
      playedOn: this.game?.played_on ?? null,
      today,
    };
  },
  computed: {
    boardOptions() {
      const boards = this.$store.state.umDbBoardList.map((board) => {
        return { text: board.name, value: board.slug };
      });
      return [{ text: "None", value: null }, ...boards];
    },
    isValid() {
      return this.myCharacterId && this.vsCharacterId && this.winner !== null;
    },
  },
  watch: {
    isValid: {
      immediate: true,
      handler(value) {
        this.$emit("update:isValid", value);
      },
    },
  },
  methods: {
    lookupMyCharacters() {
      this.myCharacterId = null;
      this.lookupCharacters(this.myCharacter);
    },
    lookupVsCharacters() {
      this.vsCharacterId = null;
      this.lookupCharacters(this.vsCharacter);
    },
    lookupCharacters(filter) {
      api.getTrackableDecks({ filter }).then((data) => {
        this.characters = data;
      });
    },
    async saveGame() {
      const gameData = {
        played_as: this.myCharacterId,
        played_vs: this.vsCharacterId,
        won: this.winner,
        went_first: this.first,
        board: this.boardSlug,
        opponent_name: this.opponentName,
        is_public: this.isPublic,
        is_counted: this.isCounted,
        winner_hp: this.winner ? this.myHp : this.vsHp,
        loser_hp: this.winner ? this.vsHp : this.myHp,
        rounds: this.rounds,
        rating: this.rating,
        notes: this.notes,
        played_on: this.playedOn,
      };
      try {
        await api.trackGame(gameData);
        this.$root.$bvToast.toast("Your game has been recorded", {
          title: "Success",
          variant: "success",
        });
        this.$router.push({
          name: "UserResultsGrid",
          params: { username: this.$store.state.userProfile.username },
        });
      } catch (err) {
        this.$root.$bvToast.toast(err.response.statusText, {
          title: "Error recording game",
          variant: "danger",
        });
      }
    },
    async updateGame() {
      const gameData = {
        played_as: this.myCharacterId,
        played_vs: this.vsCharacterId,
        won: this.winner,
        went_first: this.first,
        board: this.boardSlug,
        opponent_name: this.opponentName,
        is_public: this.isPublic,
        is_counted: this.isCounted,
        winner_hp: this.winner ? this.myHp : this.vsHp,
        loser_hp: this.winner ? this.vsHp : this.myHp,
        rounds: this.rounds,
        rating: this.rating,
        notes: this.notes,
        played_on: this.playedOn,
      };
      try {
        await api.updateGame(this.gameId, gameData);
        this.$root.$bvToast.toast("Your game has been updated", {
          title: "Success",
          variant: "success",
        });
        this.$emit("updated");
      } catch (err) {
        this.$root.$bvToast.toast(err.response.statusText, {
          title: "Error updating game",
          variant: "danger",
        });
      }
    },
  },
};
</script>
